if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

    
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

/*------------- Moblie-button ------------*/

(function () {

  var arrowUp   = '<svg width="12" height="12" viewBox="0 0 10 6" fill="none"><path d="M1 5L5 1L9 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  var arrowDown = '<svg width="12" height="12" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  var isOpen        = false;
  var initialized   = false;
  var scrollBound   = false;
  var resizeTimer   = null;

  
  var suppressAutoOpen = false;
  var suppressTimer    = null;

  var lastActiveSlide  = 0;

  var btn, section, mediaWrapper, mediaGallery, footerBar, infoBtn;
  var cachedExtraSections = [];
  var cachedMediaEls      = [];
  var cachedImgEls        = [];

  var _isMobile = window.innerWidth <= 749;
  function isMobile() { return _isMobile; }

  if (window.innerWidth <= 749) {
    var earlyStyle = document.getElementById('shop-toggle-early');
    if (!earlyStyle) {
      earlyStyle = document.createElement('style');
      earlyStyle.id = 'shop-toggle-early';
      earlyStyle.textContent = [
        '@media(max-width:749px){',
          '.product-extra-section{display:none!important}',
          '.shop-footer-bar{',
            'position:fixed!important;',
            'bottom:0!important;',
            'left:0!important;',
            'right:0!important;',
            'width:100%!important;',
            'z-index:9999!important;',
            'display:flex!important;',
          '}',
          // 'body.gallery-fullview{overflow:hidden!important;height:100svh!important}',//
          'body.gallery-fullview .shop-footer-bar{',
            'display:flex!important;',
          '}',
          'body.gallery-fullview .shop-footer-bar .shop-footwear{',
            'flex:1!important;',
          '}',
          'body.gallery-fullview .product__media-wrapper{',
            'height:var(--shop-media-h,calc(100svh - var(--shop-btn-h,56px)))!important;',
            // 'overflow:hidden!important;',
          '}',
          'body.gallery-fullview .slider-buttons,',
          'body.gallery-fullview .product__media-list .slider-counter,',
          'body.gallery-fullview media-gallery .slider-buttons,',
          'body.gallery-fullview .product__media-pagination{',
            'display:none!important;',
          '}',
          'body.gallery-fullview .product__media-list{',
            'display:block!important;',
            'padding:0!important;',
            'margin:0!important;',
            'overflow:hidden!important;',
            'scroll-snap-type:none!important;',
          '}',
          'body.gallery-fullview .product__media-list > li:not(.shop-active-slide){',
            'display:none!important;',
          '}',
          'body.gallery-fullview .product__media-list > li.shop-active-slide{',
            'width:100%!important;',
            'max-width:100%!important;',
            'margin:0!important;',
            'transform:none!important;',
            'scroll-snap-align:none!important;',
          '}',
          'body.gallery-fullview media-gallery,',
          'body.gallery-fullview .product__media-list{',
            'pointer-events:none!important;',
          '}',
          'body.gallery-fullview .product__media-wrapper .product__media img,',
          'body.gallery-fullview .product__media-wrapper .media,',
          'body.gallery-fullview .product__media-wrapper .product-media-container{',
            'width:100%!important;',
          '}',
        '}'
      ].join('');
      (document.head || document.documentElement).insertBefore(earlyStyle, (document.head || document.documentElement).firstChild);
    }
  }

  var mainSectionFound = false;
  var observer = null;

  function startObserver() {
    if (!isMobile()) return;
    var root = document.body || document.documentElement;
    observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var nodes = mutations[m].addedNodes;
        for (var i = 0; i < nodes.length; i++) {
          var node = nodes[i];
          if (node.nodeType !== 1) continue;
          if (!mainSectionFound &&
              (node.querySelector('.product__info-container') || node.querySelector('product-form'))) {
            mainSectionFound = true;
            continue;
          }
          if (mainSectionFound && node.classList.contains('shopify-section') &&
              !node.classList.contains('product-extra-section')) {
            node.classList.add('product-extra-section');
            node.style.setProperty('display', 'none', 'important');
            cachedExtraSections.push(node);
          }
        }
      }
    });
    observer.observe(root, { childList: true });
  }

  function markExistingSections() {
    if (!isMobile()) return;
    var found = false;
    var sections = document.querySelectorAll('.shopify-section');
    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      if (sec.querySelector('.product__info-container') || sec.querySelector('product-form')) {
        found = true;
        mainSectionFound = true;
        continue;
      }
      if (found && !sec.classList.contains('product-extra-section')) {
        sec.classList.add('product-extra-section');
        sec.style.setProperty('display', 'none', 'important');
        cachedExtraSections.push(sec);
      }
    }
  }

  function cacheMediaElements() {
    cachedMediaEls = Array.prototype.slice.call(
      document.querySelectorAll('.product__media-wrapper .media, .product__media-wrapper .product-media-container')
    );
    cachedImgEls = Array.prototype.slice.call(
      document.querySelectorAll('.product__media-wrapper .product__media img')
    );
  }

  function getCalculatedHeight() {
    var img = document.querySelector('.product__media-wrapper .product__media img');
    if (!img) return null;
    var imgW = parseInt(img.getAttribute('width'),  10);
    var imgH = parseInt(img.getAttribute('height'), 10);
    if (!imgW || !imgH) return null;
    var container = img.closest('.product-media-container, .media, .product__media-item, .product__media-wrapper');
    var containerWidth = (container && container.offsetWidth > 0) ? container.offsetWidth : window.innerWidth;
    return Math.round(containerWidth * (imgH / imgW));
  }

  function getBtnHeight() {
    if (btn) {
      var h = btn.getBoundingClientRect().height;
      if (h > 0) return 75;
    }
    var cssVal = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--shop-btn-h'));
    return cssVal > 0 ? 75 : 75;
  }

  function applyHeight(h) {
    for (var i = 0; i < cachedMediaEls.length; i++) {
      cachedMediaEls[i].style.setProperty('height', h, 'important');
    }
    for (var j = 0; j < cachedImgEls.length; j++) {
      cachedImgEls[j].style.setProperty('height', '100%', 'important');
      cachedImgEls[j].style.setProperty('object-fit', 'cover', 'important');
    }
  }

 function setOpenHeight() {
  var h = getCalculatedHeight();
  applyHeight(h ? ((h - 100) + 'px') : 'calc(100vw - 100px)');
}

  function setCloseHeight() {
    var btnH      = getBtnHeight();

    var rectTop   = mediaWrapper ? mediaWrapper.getBoundingClientRect().top : 0;
    var scrolled  = window.pageYOffset || window.scrollY || 0;
    var topOffset = rectTop + scrolled;
    var availH    = window.innerHeight - topOffset - btnH;
    if (availH < 100) availH = window.innerHeight - btnH;
    applyHeight(availH + 'px');
    document.documentElement.style.setProperty('--shop-media-h', availH + 'px');
  }

  function showExtras() {
    cachedExtraSections = Array.prototype.slice.call(document.querySelectorAll('.product-extra-section'));
    for (var i = 0; i < cachedExtraSections.length; i++) {
      cachedExtraSections[i].style.setProperty('display', 'block', 'important');
    }

    void document.body.offsetHeight;

    function domReinsert(el) {
      var parent  = el.parentNode;
      var anchor  = el.nextSibling;
      if (!parent) return;
      parent.removeChild(el);
      void parent.offsetHeight;    
      parent.insertBefore(el, anchor); 
    }

    setTimeout(function () {
      for (var i = 0; i < cachedExtraSections.length; i++) {
        domReinsert(cachedExtraSections[i]);
      }

      void document.body.offsetHeight;

      var savedY = window.pageYOffset || window.scrollY || 0;
      window.scrollBy(0, 1);
      requestAnimationFrame(function () {
        window.scrollBy(0, -1);
        window.dispatchEvent(new Event('scroll'));
        window.dispatchEvent(new Event('resize'));
      });
    }, 50);

    var retries = [300, 700, 1200];
    for (var d = 0; d < retries.length; d++) {
      (function (delay) {
        setTimeout(function () {
          if (!isOpen) return;
          window.dispatchEvent(new Event('scroll'));
          window.dispatchEvent(new Event('resize'));
        }, delay);
      })(retries[d]);
    }

    var recs = document.querySelectorAll('product-recommendations');
    for (var r = 0; r < recs.length; r++) {
      var rec = recs[r];
      if (!rec.dataset.shopLoaded && rec.dataset.url) {
        rec.dataset.shopLoaded = 'true';
        (function(el) {
          fetch(el.dataset.url)
            .then(function(res) { return res.text(); })
            .then(function(html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
            })
            .catch(function() {});
        })(rec);
      }
    }
  }

  function hideExtras() {
    for (var i = 0; i < cachedExtraSections.length; i++) {
      cachedExtraSections[i].style.setProperty('display', 'none', 'important');
    }
  }

  function disableSliders() {
    document.body.classList.add('gallery-fullview');
    var dots = document.querySelector('.carousel-img-dots');
    if (dots) dots.style.display = 'none';
  }

  function enableSliders() {
    document.body.classList.remove('gallery-fullview');
    var dots = document.querySelector('.carousel-img-dots');
    if (dots) dots.style.display = '';
  }

  function getActiveSlideIndex(list) {
    if (!list) return 0;
    var items = list.querySelectorAll('.product__media-list-item');
    if (!items.length) items = list.children;
    if (items.length < 2) return 0;
    var box = list.getBoundingClientRect();
    var center = box.left + box.width / 2;
    var best = 0, bestDist = Infinity;
    for (var i = 0; i < items.length; i++) {
      var r = items[i].getBoundingClientRect();
      if (!r.width) continue;               
      var d = Math.abs((r.left + r.width / 2) - center);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }

  function alignSliderToIndex(list, index) {
    if (!list) return;
    var items = list.querySelectorAll('.product__media-list-item');
    if (!items.length) items = list.children;
    if (!items.length) return;
    if (index < 0) index = 0;
    if (index > items.length - 1) index = items.length - 1;
    var target = items[index].offsetLeft - items[0].offsetLeft;
    var prevBehavior = list.style.scrollBehavior;
    list.style.scrollBehavior = 'auto'; 
    list.scrollLeft = target;
    try { list.dispatchEvent(new Event('scroll')); } catch (e) {}
    requestAnimationFrame(function () {
      list.scrollLeft = target;
      list.style.scrollBehavior = prevBehavior || '';
    });
  }

  function markActiveSlide(list, index) {
    if (!list) return;
    var items = list.querySelectorAll('.product__media-list-item');
    if (!items.length) items = list.children;
    if (!items.length) return;
    if (index < 0) index = 0;
    if (index > items.length - 1) index = items.length - 1;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList) items[i].classList.toggle('shop-active-slide', i === index);
    }
  }

  function syncBtnHeightVar() {
    if (!btn) return;
    var h = btn.getBoundingClientRect().height || 56;
    document.documentElement.style.setProperty('--shop-btn-h', h + 'px');
  }

  function resetDesktop() {
    if (section)      section.style.display = '';
    if (btn)          { btn.classList.remove('full-view'); btn.innerHTML = 'SHOP THIS FOOTWEAR ' + arrowUp; }
    if (mediaWrapper) mediaWrapper.classList.remove('gallery-section-open');
    for (var i = 0; i < cachedExtraSections.length; i++) cachedExtraSections[i].style.display = '';
    for (var j = 0; j < cachedMediaEls.length; j++)      cachedMediaEls[j].style.height = '';
    for (var k = 0; k < cachedImgEls.length; k++) {
      cachedImgEls[k].style.height = '';
      cachedImgEls[k].style.objectFit = '';
    }
    document.body.style.overflow = '';
    document.body.style.height   = '';
    enableSliders();
    isOpen = false;
  }

  function init() {
    if (initialized) return;
    if (observer) observer.disconnect();

    if (!isMobile()) {
      resetDesktop();
      initialized = true;
      return;
    }

    btn          = document.querySelector('.shop-footwear');
    section      = document.querySelector('.product__info-container');
    mediaWrapper = document.querySelector('.product__media-wrapper');
    mediaGallery = document.querySelector('media-gallery');

    if (!btn || !section) {
      requestAnimationFrame(function () { initialized = false; init(); });
      return;
    }

    initialized = true;
    markExistingSections();
    cacheMediaElements();

    section.id    = 'product-info-toggle-section';
    btn.innerHTML = 'SHOP THIS FOOTWEAR ' + arrowUp;
    syncBtnHeightVar();

    footerBar = document.querySelector('.shop-footer-bar');
    if (!footerBar) {
      footerBar = document.createElement('div');
      footerBar.className = 'shop-footer-bar';
      document.body.appendChild(footerBar);
    }
    if (btn.parentNode !== footerBar) {
      footerBar.appendChild(btn);
    }
    infoBtn = document.querySelector('.shop-footwear-info');
    if (!infoBtn) {
      infoBtn = document.createElement('button');
      infoBtn.className = 'shop-footwear-info';
      infoBtn.innerHTML = '<svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8"/><line x1="12" y1="12" x2="12" y2="16"/></svg>';
      infoBtn.style.cssText = [
        'width:45px',
        'height:45px',
        'background:#EAEAEA',
        'border:none',
        'border-radius:8px',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'cursor:pointer',
        'order:2;',
        'flex-shrink:0'
      ].join(';');
      footerBar.appendChild(infoBtn);
    }

    section.style.setProperty('display', 'none', 'important');
    hideExtras();
    var sliderListInit = document.querySelector('.product__media-list');
    markActiveSlide(sliderListInit, getActiveSlideIndex(sliderListInit));
    setCloseHeight();
    disableSliders();

    var imgEl = document.querySelector('.product__media-wrapper .product__media img');
    if (imgEl && !imgEl.complete) {
      imgEl.addEventListener('load', function () {
        cacheMediaElements();
        if (!isOpen) setCloseHeight();
      }, { once: true });
    }

    function openGallery() {
      if (isOpen) return;
      isOpen = true;
      syncBtnHeightVar();

      setOpenHeight();
      mediaWrapper && mediaWrapper.classList.add('gallery-section-open');
      section.style.setProperty('display', 'block', 'important');
      showExtras();
      enableSliders();
      document.body.style.overflow = '';
      document.body.style.height   = '';
      btn.classList.add('full-view');
      btn.innerHTML = 'SEE FULL VIEW ' + arrowDown;
      infoBtn.style.display = 'none';
      if (mediaGallery && mediaGallery.parentNode) {
        mediaGallery.parentNode.insertBefore(btn, mediaGallery.nextSibling);
      }

      var sliderList = document.querySelector('.product__media-list');
      alignSliderToIndex(sliderList, lastActiveSlide);
      requestAnimationFrame(function () { alignSliderToIndex(sliderList, lastActiveSlide); });
    }

    function closeGallery() {
      if (!isOpen) return;
      isOpen = false;

      var sliderList  = document.querySelector('.product__media-list');
      var activeSlide = getActiveSlideIndex(sliderList);
      lastActiveSlide = activeSlide;
      markActiveSlide(sliderList, activeSlide);  

      suppressAutoOpen = true;
      clearTimeout(suppressTimer);

      syncBtnHeightVar();

      btn.classList.remove('full-view');
      btn.innerHTML = 'SHOP THIS FOOTWEAR ' + arrowUp;
      infoBtn.style.display = 'flex';
      mediaWrapper && mediaWrapper.classList.remove('gallery-section-open');
      footerBar.appendChild(btn);
      document.body.appendChild(footerBar);
      section.style.setProperty('display', 'none', 'important');
      hideExtras();
      disableSliders();

  
      window.scrollTo(0, 0);

      requestAnimationFrame(function () {
        syncBtnHeightVar();
        setCloseHeight();
        markActiveSlide(sliderList, activeSlide);  
      });

    
      setTimeout(function () {
        if (!isOpen) {
          syncBtnHeightVar();
          setCloseHeight();
          disableSliders();
          markActiveSlide(sliderList, activeSlide);
        }
      }, 200)
      suppressTimer = setTimeout(function () { suppressAutoOpen = false; }, 500);
    }

    if (!btn.dataset.shopToggleBound) {
      btn.dataset.shopToggleBound = '1';
      btn.addEventListener('click', function () {
        if (isOpen) {
          closeGallery();
        } else {
      
          if (suppressAutoOpen) return;
          openGallery();
        }
      });
    }

    if (!scrollBound) {
      scrollBound = true;
      var touchStartY = null;

      function atTop() {
        return (window.pageYOffset || window.scrollY || 0) <= 2;
      }

      
      window.addEventListener('scroll', function () {
        if (!isMobile() || isOpen) return;
    
        if (suppressAutoOpen) return;
        if ((window.pageYOffset || window.scrollY || 0) > 30) openGallery();
      }, { passive: true });

  
      window.addEventListener('wheel', function (e) {
        if (!isMobile()) return;
        if (isOpen) {
          
          if (atTop() && e.deltaY < -10) closeGallery();
        } else {
          if (suppressAutoOpen) return;
          if (e.deltaY > 10) openGallery();
        }
      }, { passive: true });

      // touch
      window.addEventListener('touchstart', function (e) {
        if (!isMobile()) return;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      window.addEventListener('touchmove', function (e) {
        if (!isMobile() || touchStartY === null) return;
        var delta = touchStartY - e.touches[0].clientY;
        if (isOpen) {
          if (atTop() && delta < -50) {
            closeGallery();
            touchStartY = null;
          }
        } else {
          if (suppressAutoOpen) return;
          if (delta > 40) {
            openGallery();
            touchStartY = null;
          }
        }
      }, { passive: true });

      window.addEventListener('touchend', function () {
        touchStartY = null;
      }, { passive: true });
      
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', function () {
          if (!isMobile()) return;
          if (isOpen) setOpenHeight();
          else        setCloseHeight();
        });
      }
    }

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        _isMobile = window.innerWidth <= 749;
        if (!isMobile()) {
          resetDesktop();
        } else {
          syncBtnHeightVar();
          cacheMediaElements();
          if (isOpen) setOpenHeight();
          else        setCloseHeight();
        }
      }, 150);
    });

    if (window.ResizeObserver && cachedMediaEls.length > 0) {
      var ro = new ResizeObserver(function () {
        if (!isMobile() || !isOpen) return;
        setOpenHeight();
      });
      ro.observe(cachedMediaEls[0]);
    }
  }

  startObserver();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', function () {
    if (!initialized) init();
    if (isMobile()) {
      syncBtnHeightVar();
      cacheMediaElements();
      requestAnimationFrame(function () {
        syncBtnHeightVar();
        if (isOpen) setOpenHeight();
        else        setCloseHeight();
      });
      setTimeout(function () {
        if (!isOpen) { syncBtnHeightVar(); setCloseHeight(); }
      }, 100);
      setTimeout(function () {
        if (!isOpen) { syncBtnHeightVar(); setCloseHeight(); }
      }, 400);
      setTimeout(function () {
        if (!isOpen) { syncBtnHeightVar(); setCloseHeight(); }
      }, 800);
    }
  }, { once: true });

}());

/* ---------- Add to Button ---------- */
(function () {
  var style = document.createElement('style');
  style.textContent = '.uk-size-converter {display: none !important;}';
  document.head.appendChild(style);

  var variantJustChanged = false;
  var variantSyncGuardTimer = null;

  function resetButtonState(submitBtn) {
    if (!submitBtn) return;
    submitBtn.dataset.itemAdded = 'false';
    var btnSpan = submitBtn.querySelector('span');
    if (btnSpan) btnSpan.textContent = 'Add to Bag';
    submitBtn.classList.remove('custom-go', 'custom-add-cart');
  }
  function resetSizeSelectionOnLoad() {
    document.querySelectorAll('variant-selects input[type="radio"]').forEach(function (input) {
      input.checked = false;
    });
    resetButtonState(document.querySelector('.product-form__submit'));
    var qtyBox = document.querySelector('.product-form__quantity');
    if (qtyBox) {
      qtyBox.style.removeProperty('display');
      qtyBox.classList.remove('size-selected');
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resetSizeSelectionOnLoad);
  } else {
    resetSizeSelectionOnLoad();
  }

  document.addEventListener('change', function (e) {
    if (!e.target.matches('variant-selects input[type="radio"]')) return;
    var form = e.target.closest('product-form') || document;
    var submitBtn = form.querySelector('.product-form__submit');
    var qtyBox = form.querySelector('.product-form__quantity');
    var ukConverter = document.querySelector('.uk-size-converter');
    resetButtonState(submitBtn);
    if (submitBtn) submitBtn.classList.add('custom-add-cart');
    if (qtyBox) {
      qtyBox.style.setProperty('display', 'block', 'important');
      qtyBox.classList.add('size-selected');
    }
    style.textContent = '';
    if (ukConverter) ukConverter.style.setProperty('display', 'block', 'important');

    variantJustChanged = true;
    clearTimeout(variantSyncGuardTimer);
    variantSyncGuardTimer = setTimeout(function () {
      variantJustChanged = false;
    }, 300);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event?.source && event.source !== 'product-form') return;
      var submitBtn = document.querySelector('.product-form__submit');
      if (!submitBtn) return;
      submitBtn.dataset.itemAdded = 'true';
      var btnSpan = submitBtn.querySelector('span');
      if (btnSpan) btnSpan.textContent = 'Go to Bag';
      submitBtn.classList.remove('custom-add-cart');
      submitBtn.classList.add('custom-go');
    });
  }

  document.addEventListener('click', function (e) {
    var submitBtn = e.target.closest('.product-form__submit');
    if (!submitBtn) return;
    if (submitBtn.dataset.itemAdded === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        window.location.href = '/cart';
      }
      return;
    }
    if (variantJustChanged) {
      e.preventDefault();
      e.stopImmediatePropagation();
      setTimeout(function () {
        variantJustChanged = false;
        submitBtn.click();
      }, 300);
    }
  }, true);
  function showAddToBag() {
    if (btnSpan) btnSpan.textContent = 'Add to Bag';
    submitBtn.style.removeProperty('background');
    submitBtn.style.removeProperty('color');
    submitBtn.classList.remove('custom-go');
    submitBtn.classList.remove('custom-add-cart');
  }
})();

// First accordion open on desktop (>749px)
(function () {
  function openFirstAccordion() {
    if (window.innerWidth <= 749) return;
    var first = document.querySelector('[data-first-accordion="true"]');
    if (first && !first.hasAttribute('open')) {
      first.setAttribute('open', '');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFirstAccordion);
  } else {
    openFirstAccordion();
  }

  window.addEventListener('resize', function () {
    openFirstAccordion();
  });
})();